// Карта отчёта о пропусках: точки судов их цветами, пропуски пунктиром с
// номером (у порта — серым: там молчание норма), круг радиуса соседей у
// пропуска в море и точки соседей с сигналом. Карта своя, как у «Квот»,
// поэтому рубежи — общим хуком и общими тумблерами.

import { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import type { AisGapResult } from '../api'
import { domainMaxBounds } from '../domain/limits'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { createStyleGate } from '../map/styleGate'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { MapGraticule } from '../map/MapGraticule'
import { boxKey, boundsToBox, useBoundaryOverlays } from '../map/useBoundaryOverlays'
import type { BBox } from '../api'
import { useT } from '../i18n'
import { GAP_COLOR, NEIGHBOR_COLOR, PORT_GAP_COLOR } from './ganttLayout'
import { dataBounds, gapCircles, gapLines, neighborPoints, vesselPoints } from './gapsMapLayers'

const CIRCLES = 'rg-circles'
/** Снизу вверх: соседи под судами, пропуски и номера — поверх всего. */
const IDS = [CIRCLES, 'rg-neighbors', 'rg-vessels', 'rg-gaps', 'rg-gap-labels'] as const

type Data = ReturnType<typeof layerData>

function layerData(result: AisGapResult, colors: Map<number, string>) {
  const { lines, labels } = gapLines(result)
  return { circles: gapCircles(result), gaps: lines, labels,
           vessels: vesselPoints(result, colors), neighbors: neighborPoints(result) }
}

/** Номер пропуска картинкой: сервера глифов у карты кабинета нет
 *  (см. подписи изобат в `map/MapCanvas.tsx`). */
function addNumber(map: maplibregl.Map, no: number, port: boolean) {
  const id = `rg-no-${no}-${port ? 'p' : 's'}`
  if (map.hasImage(id)) return
  const r = 2
  const text = String(no)
  const w = (10 + 7 * text.length) * r
  const h = 16 * r
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = port ? PORT_GAP_COLOR : GAP_COLOR
  ctx.beginPath()
  ctx.roundRect(0, 0, w, h, 4 * r)
  ctx.fill()
  ctx.fillStyle = '#ffffff'
  ctx.font = `600 ${11 * r}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, w / 2, h / 2 + r)
  map.addImage(id, ctx.getImageData(0, 0, w, h), { pixelRatio: r })
}

function apply(map: maplibregl.Map, d: Data) {
  for (const f of d.labels.features) addNumber(map, f.properties!.no, Boolean(f.properties!.port))
  const set = (id: string, data: GeoJSON.FeatureCollection) => {
    const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined
    if (src) src.setData(data)
    else map.addSource(id, { type: 'geojson', data })
  }
  set(CIRCLES, d.circles)
  set('rg-gaps', d.gaps)
  set('rg-vessels', d.vessels)
  set('rg-neighbors', d.neighbors)
  set('rg-gap-labels', d.labels)
  if (map.getLayer(CIRCLES)) return
  map.addLayer({ id: CIRCLES, type: 'line', source: CIRCLES,
    paint: { 'line-color': NEIGHBOR_COLOR, 'line-width': 1.2, 'line-opacity': 0.7, 'line-dasharray': [3, 2] } })
  map.addLayer({ id: 'rg-neighbors', type: 'circle', source: 'rg-neighbors',
    paint: { 'circle-radius': 1.8, 'circle-color': NEIGHBOR_COLOR, 'circle-opacity': 0.55 } })
  map.addLayer({ id: 'rg-gaps', type: 'line', source: 'rg-gaps',
    paint: { 'line-color': ['case', ['get', 'port'], PORT_GAP_COLOR, GAP_COLOR],
             'line-width': 2.2, 'line-dasharray': [2, 1.5] } })
  map.addLayer({ id: 'rg-vessels', type: 'circle', source: 'rg-vessels',
    paint: { 'circle-radius': 2.6, 'circle-color': ['get', 'color'], 'circle-opacity': 0.95 } })
  map.addLayer({ id: 'rg-gap-labels', type: 'symbol', source: 'rg-gap-labels',
    layout: { 'icon-image': ['concat', 'rg-no-', ['to-string', ['get', 'no']], '-',
                             ['case', ['get', 'port'], 'p', 's']],
              'icon-allow-overlap': true, 'icon-offset': [0, -14] } })
}

interface Props {
  result: AisGapResult
  colors: Map<number, string>
  /** Пропуск, к которому вести карту (клик в Ганте или таблице). */
  focus: { no: number; nonce: number } | null
}

export function GapsMap({ result, colors, focus }: Props) {
  const t = useT()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const gateRef = useRef(createStyleGate())
  const [box, setBox] = useState<BBox | null>(null)
  const [on, setOn] = useState({ eez: false, districts: false })
  const boundaryMeta = useBoundaryOverlays({ mapRef, gateRef, on, viewKey: boxKey(box), beforeId: CIRCLES })
  const data = useMemo(() => layerData(result, colors), [result, colors])

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      attributionControl: false,
      center: [150, 52],
      zoom: 3,
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    setMapInstance(map)
    const gate = createStyleGate()
    gateRef.current = gate
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.on('moveend', () => setBox(boundsToBox(map)))
    map.on('load', () => { setBox(boundsToBox(map)); gate.markReady() })
    const unregister = registerBasemapMap(map)
    return () => { unregister(); map.remove(); mapRef.current = null; setMapInstance(null) }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      apply(map, data)
      for (const id of IDS) if (map.getLayer(id)) map.moveLayer(id)
      const b = dataBounds(result)
      if (b) map.fitBounds(b, { padding: 40, maxZoom: 9, duration: 0 })
    })
  }, [data, result])

  useEffect(() => {
    const map = mapRef.current
    const gap = focus && result.gaps.find(g => g.no === focus.no)
    const end = gap?.before ?? gap?.after
    if (!map || !end) return
    map.flyTo({ center: [end[1], end[0]], zoom: Math.max(map.getZoom(), 8) })
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [focus, result])

  return (
    <div className="relative h-[520px] w-full overflow-hidden rounded-xl border border-hull">
      {/* Высота, а НЕ `absolute inset-0` — сторож `mapContainer.test.ts`. */}
      <div ref={containerRef} className="h-full w-full" />
      <MapGraticule map={mapInstance} />
      <BasemapAttribution corner="br" />
      <div className="absolute left-3 top-3 z-10 space-y-1.5 rounded-lg border border-hull bg-abyss/95 px-3 py-2 text-[12px] text-mist">
        <Legend color={GAP_COLOR} dashed label={t('reports.map.gap')} />
        <Legend color={PORT_GAP_COLOR} dashed label={t('reports.map.portGap')} />
        <Legend color={NEIGHBOR_COLOR} ring label={t('reports.map.circle', { km: result.radius_km })} />
        <Legend color={NEIGHBOR_COLOR} dot label={t('reports.map.neighbor')} />
        <div className="border-t border-hull pt-1.5">
          <BoundaryToggles on={on} meta={boundaryMeta}
            onToggle={k => setOn(s => ({ ...s, [k]: !s[k] }))} />
        </div>
      </div>
    </div>
  )
}

function Legend({ color, label, dashed, ring, dot }: {
  color: string; label: string; dashed?: boolean; ring?: boolean; dot?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <svg aria-hidden width="18" height="10" className="shrink-0">
        {dot && <circle cx="9" cy="5" r="3" fill={color} />}
        {ring && <circle cx="9" cy="5" r="4" fill="none" stroke={color} strokeDasharray="2 1.5" />}
        {dashed && <line x1="1" y1="5" x2="17" y2="5" stroke={color} strokeWidth="2" strokeDasharray="3 2" />}
      </svg>
      <span>{label}</span>
    </div>
  )
}
